import { DuplicateIcon, EditIcon, EllipsisIcon, EmailIcon, TrashIcon } from "@xola/icons";
import { flash, PopoverList, Tooltip } from "@xola/ui-kit";
import React, { useRef, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import { AmountType, EmailTemplate, GiftOffer, GiftOfferType, PurchaseRule } from "types";
import { api } from "../../api/api";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useResource } from "../../hooks/useResource";
import { useVisibility } from "../../hooks/useVisibility";
import { Currency } from "../Common/Currency";
import { NoTranslate } from "../Common/NoTranslate";
import { ProductImage } from "../Common/ProductImage";
import { PromptModal } from "../Common/PromptModal";
import { SecondaryButton } from "../Common/SecondaryButton";
import { PreviewMessageTemplate } from "../Customers/Templates/PreviewMessageTemplate";
import { getTerms } from "./EditGiftOffer";

export const GiftOfferTile = ({ giftOffer }: { giftOffer: GiftOffer }) => {
    const history = useHistory();

    const excerptRef = useRef<HTMLDivElement | null>(null);
    const element = excerptRef.current?.childNodes?.[0] as HTMLElement | null;
    const isTruncated = element ? element.scrollHeight > element.clientHeight : false;

    const handleNavigation = (route: string) => {
        history.push(`/products/giftOffers/${giftOffer.id}/${route}`);
    };

    return (
        <div
            className="group flex cursor-pointer rounded p-2"
            data-testid="gift-offer-tile"
            onClick={() => handleNavigation("description")}
        >
            <ProductImage product={giftOffer} size="large" />
            <div className="w-full">
                <div className="min-h-8 flex justify-between">
                    <NoTranslate className="text-lg font-bold line-clamp-2">{giftOffer.name}</NoTranslate>
                    <div className="flex min-w-24 justify-end space-x-2">
                        <SecondaryButton
                            size="small"
                            icon={<EditIcon />}
                            className="invisible group-hover:visible"
                            onClick={() => handleNavigation("description")}
                        >
                            Edit
                        </SecondaryButton>
                        <GiftOfferActions giftOffer={giftOffer} handleNavigation={handleNavigation} />
                    </div>
                </div>

                <Tooltip
                    disabled={!isTruncated}
                    content={<NoTranslate data-testid="gift-tile-tooltip-desc">{giftOffer.desc}</NoTranslate>}
                >
                    <div ref={excerptRef}>
                        <NoTranslate className="pt-1 text-base line-clamp-2" data-testid="gift-offer-tile-desc">
                            {giftOffer.desc}
                        </NoTranslate>
                    </div>
                </Tooltip>
            </div>
        </div>
    );
};

export const GiftOfferTerm = ({ giftOffer }: { giftOffer: GiftOffer }) => {
    const { data: purchaseRule } = useResource<PurchaseRule>(["purchaseRules", giftOffer?.purchaseRule?.id]);

    if (giftOffer.baseGiftProduct) {
        return null;
    }

    const item = { ...giftOffer, ...getTerms(giftOffer, purchaseRule) };

    return (
        <span className="text-base line-clamp-3">
            {item.type === GiftOfferType.FIXED || item.experience ? (
                <Currency>{Math.abs(item.price ?? item.purchasePrice ?? 0)}</Currency>
            ) : (
                <>
                    {item.discountAmountType === AmountType.ABSOLUTE ? (
                        <span>
                            <Currency>{item.discountAmount ?? 0}</Currency> discount
                        </span>
                    ) : (
                        <>
                            <NoTranslate>{item.discountAmount}%</NoTranslate> discount
                        </>
                    )}
                    {item.minimumPrice ? (
                        <span className="ml-1">
                            {" "}
                            promo on <Currency>{item.minimumPrice ?? 0}</Currency>+
                        </span>
                    ) : null}
                </>
            )}
        </span>
    );
};

const GiftOfferActions = ({
    giftOffer,
    handleNavigation,
}: {
    giftOffer: GiftOffer;
    handleNavigation: (route: string) => void;
}) => {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [visible, setVisible] = useState(false);
    const invalidate = useInvalidate();
    const [isEmailPreviewVisible, { toggle: toggleEmailPreview }] = useVisibility(false);

    const { mutateAsync: deleteGiftOffer, isLoading } = useMutation(async (id: ID) => {
        return api.delete(`/giftOffers/${id}`);
    });

    const toggleVisibility = (e: any) => {
        e.stopPropagation();
        setVisible(!visible);
    };

    const handleToggleDelete = async (e: any) => {
        e.stopPropagation();
        setIsDeleteModalOpen(!isDeleteModalOpen);
    };

    const handleDeleteConfirm = async () => {
        if (giftOffer?.id) {
            await deleteGiftOffer(giftOffer.id);
            invalidate("giftOffers");
            flash.success("Gift offer deleted");
        }

        setIsDeleteModalOpen(false);
    };

    const handlePreviewEmail = (e: any) => {
        e.stopPropagation();
        toggleEmailPreview();
    };

    return (
        <>
            <PopoverList zIndex="10" visible={visible} onClickOutside={() => setVisible(false)}>
                <SecondaryButton size="small" onClick={toggleVisibility}>
                    <EllipsisIcon />
                </SecondaryButton>
                <PopoverList.Item name="Preview Email" onClickItem={handlePreviewEmail}>
                    <EmailIcon />
                    <span>Preview Email</span>
                </PopoverList.Item>
                <PopoverList.Item
                    isDisabled
                    name="Manage Photos"
                    onClick={(e: any) => {
                        e.stopPropagation();
                        handleNavigation("photos");
                    }}
                >
                    <DuplicateIcon />
                    <span>Manage Photos</span>
                </PopoverList.Item>
                <PopoverList.Item name="Delete" onClick={handleToggleDelete}>
                    <TrashIcon />
                    <span>Delete</span>
                </PopoverList.Item>
            </PopoverList>

            <PreviewMessageTemplate
                isOpen={isEmailPreviewVisible}
                payload={{
                    template: EmailTemplate.PURCHASE_GIFT_CERTIFICATE,
                    giftOffer: giftOffer.id,
                }}
                onClose={() => toggleEmailPreview()}
            />

            <PromptModal
                title="Delete Gift?"
                isLoading={isLoading}
                isOpen={isDeleteModalOpen}
                onConfirm={handleDeleteConfirm}
                onClose={handleToggleDelete}
            >
                Are you sure you want to delete this gift offer? This action cannot be un-done. Existing purchases will
                be unaffected
            </PromptModal>
        </>
    );
};
